import { z } from 'zod';
import type { AlertsReportV2 } from './alerts-report-v2';
import type { NetworkMonth } from './network-month';

/** Share pages on the portal host (D-132): bundles `/b/:code` and live report links `/r/:code`. */

export const BUNDLE_KINDS = ['bad_day', 'streak', 'good_day', 'alert_group'] as const;
export type BundleKind = (typeof BUNDLE_KINDS)[number];

/** one call of a bundle — never a phone, a masked phone or a customer name */
export interface BundleItem {
  /** ISO instant of the call (CallDigest.startedAt) */
  at: string;
  /** set only when the list spans branches */
  branch: string | null;
  agentName: string | null;
  /** classifier name */
  typeLabel: string | null;
  outcomeLabel: string | null;
  /** agent score */
  score: number | null;
  /** our reason line, ≤ 160 chars */
  why: string | null;
  /** the NIVISION report (D-11: needs a NIVISION login) */
  reportUrl: string | null;
}

export const BUNDLE_WHY_MAX = 160;
/** the only host a bundle item may link to */
export const BUNDLE_REPORT_HOST = 'app.nivision.co.il';

/** https, exactly the NIVISION host (no userinfo, no port, no look-alike suffix), then a path / query / fragment or nothing */
const NIVISION_REPORT_URL_RE = /^https:\/\/app\.nivision\.co\.il(?:[/?#][^\s]*)?$/;

function isNivisionReportUrl(value: string): boolean {
  return NIVISION_REPORT_URL_RE.test(value);
}

const label = z.string().trim().min(1).max(120).nullable();

export const BundleItemSchema: z.ZodType<BundleItem> = z.strictObject({
  at: z.iso.datetime({ offset: true }),
  branch: label,
  agentName: label,
  typeLabel: label,
  outcomeLabel: label,
  score: z.number().finite().nullable(),
  why: z.string().trim().max(BUNDLE_WHY_MAX).nullable(),
  reportUrl: z
    .string()
    .trim()
    .max(2048)
    .refine(isNivisionReportUrl, { message: 'reportUrl must be an https://app.nivision.co.il link' })
    .nullable(),
});

export interface BundleDraft {
  kind: BundleKind;
  title: string;
  subtitle: string | null;
  why: string;
  items: BundleItem[];
}

export interface BundleView {
  kind: BundleKind;
  lang: 'he' | 'en';
  title: string;
  subtitle: string | null;
  why: string;
  branch: string | null;
  createdAt: string;
  expiresAt: string;
  items: BundleItem[];
}

export const REPORT_SHARE_KINDS = ['alerts_report', 'network_month'] as const;
export type ReportShareKind = (typeof REPORT_SHARE_KINDS)[number];

export const SHARE_TTL_DAYS = [7, 30, 90, 365] as const;
export type ShareTtlDays = (typeof SHARE_TTL_DAYS)[number];

export type ReportSharePeriod = 'day' | 'week' | 'month';

export interface AlertsReportShareParams {
  period: ReportSharePeriod;
  /** null = always the current period */
  date: string | null;
  branchIds: string[] | null;
}

const period = z.enum(['day', 'week', 'month']);
const anchorDate = z.iso.date().nullable();

export interface ReportShareCreate {
  clientId: string;
  period: ReportSharePeriod;
  date: string | null;
  expiresInDays: ShareTtlDays;
  requireSession: boolean;
}
export const ReportShareCreateSchema: z.ZodType<ReportShareCreate> = z.object({
  clientId: z.string().trim().min(1).max(64),
  period,
  date: anchorDate,
  expiresInDays: z.union([z.literal(7), z.literal(30), z.literal(90), z.literal(365)]),
  requireSession: z.boolean(),
});

export interface PortalReportShare {
  period: ReportSharePeriod;
  date: string | null;
  expiresInDays: 7 | 30;
}
export const PortalReportShareSchema: z.ZodType<PortalReportShare> = z.object({
  period,
  date: anchorDate,
  expiresInDays: z.union([z.literal(7), z.literal(30)]),
});

export interface ShareListItem {
  id: string;
  type: 'bundle' | 'report';
  kind: BundleKind | ReportShareKind;
  title: string;
  createdAt: string;
  expiresAt: string;
  revokedAt: string | null;
  views: number;
  lastViewedAt: string | null;
}

export type ShareView =
  | { kind: 'alerts_report'; expiresAt: string; report: AlertsReportV2 }
  | { kind: 'network_month'; expiresAt: string; month: NetworkMonth };
